import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { DigitalTierBadge } from '../components/DigitalScore';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Target, Download, ArrowRight, Users, Zap, TrendingUp, PoundSterling, ChevronDown, ChevronUp, Lock, Sparkles } from 'lucide-react';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { track } from '../lib/paywall';
import { toast } from 'sonner';

const TYPES = [
  { id: 'adoption', label: 'Digital Adoption', desc: 'Support businesses with low digital presence', icon: Users },
  { id: 'infrastructure', label: 'Infrastructure', desc: 'Improve connectivity where speeds are low', icon: Zap },
  { id: 'skills', label: 'Digital Skills', desc: 'Training for digitally invisible businesses', icon: Target },
  { id: 'ai_readiness', label: 'AI Readiness', desc: 'Prepare digitally active businesses for AI', icon: Zap },
];

export default function InterventionPlanner() {
  const [searchParams] = useSearchParams();
  const { variant } = useEntitlements();
  const [districtOptions, setDistrictOptions] = useState([]);
  const [district, setDistrict] = useState(searchParams.get('district') || '');
  const [type, setType] = useState('adoption');
  const [targetScore, setTargetScore] = useState(3);
  const [budget, setBudget] = useState(50000);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/postcodes').then(({ data }) => {
      const opts = (data || []).map(p => p.district).sort();
      setDistrictOptions(opts);
      setDistrict(prev => (prev && opts.includes(prev)) ? prev : (opts[0] || ''));
    }).catch(() => {});
  }, []);

  const handlePlan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/interventions/plan', {
        postcode_district: district,
        intervention_type: type,
        target_score: targetScore,
        budget: budget,
      });
      setResults(data);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Failed to generate intervention plan.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate intervention plan.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="intervention-planner">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink">Intervention Planner</h1>
        <p className="font-body text-base text-ink/70 font-medium mt-1">Plan Targeted Business Support</p>
      </div>
      <p className="font-body text-sm text-ink/50 max-w-2xl leading-relaxed">
        Behind every postcode is a business. Behind every business is a person whose livelihood depends on whether their customers can find them.
        Use the Intervention Planner to identify which businesses need which support — ranked by human impact per pound spent.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Config panel */}
        <div className="solid-card p-5 space-y-4">
          <h3 className="font-display text-lg font-extrabold text-ink">Configure Intervention</h3>
          
          <div>
            <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/50">Target Area</Label>
            <Select value={district} onValueChange={setDistrict}>
              <SelectTrigger data-testid="intervention-district" className="font-body text-sm mt-1"><SelectValue placeholder="Select a postcode district…" /></SelectTrigger>
              <SelectContent>
                {districtOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/50">Intervention Type</Label>
            <div className="space-y-2 mt-1">
              {TYPES.map(t => (
                <button
                  key={t.id}
                  data-testid={`intervention-type-${t.id}`}
                  onClick={() => setType(t.id)}
                  className={`w-full text-left p-3 rounded-[4px] border transition-all font-body text-xs ${
                    type === t.id ? 'border-brand-orange bg-brand-orange/5 text-ink' : 'border-ink/10 text-ink/60 hover:border-ink/20'
                  }`}
                >
                  <span className="font-medium">{t.label}</span>
                  <span className="block text-ink/40 mt-0.5">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/50" title="Businesses scoring at or below this threshold will be included in the intervention plan. Default: 3">Minimum Digital Score to Target</Label>
            <Input data-testid="intervention-target-score" type="number" min={0} max={5} value={targetScore} onChange={e => setTargetScore(Number(e.target.value))} className="font-body text-sm mt-1" />
            <p className="font-body text-[10px] text-ink/35 mt-1">Businesses scoring at or below this threshold will be included. Default: 3</p>
          </div>

          <div>
            <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/50" title="Enter the total budget available for this intervention. The planner will identify the highest-impact businesses within that budget.">Available Budget (£)</Label>
            <Input data-testid="intervention-budget" type="number" min={0} value={budget} onChange={e => setBudget(Number(e.target.value))} className="font-body text-sm mt-1" />
            <p className="font-body text-[10px] text-ink/35 mt-1">The planner will identify the highest-impact businesses within this budget.</p>
          </div>

          <Button data-testid="plan-intervention-btn" onClick={handlePlan} disabled={loading} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
            {loading ? 'Planning...' : 'Generate Intervention Plan'} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          {results ? (
            <>
              {/* Summary */}
              <div className="solid-card p-5 border-l-4 border-l-brand-orange animate-fade-in">
                <h3 className="font-display text-lg font-extrabold text-ink mb-3">Intervention Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="text-center">
                    <span className="font-body text-xs text-ink/50 block">District</span>
                    <span className="font-display text-xl font-extrabold text-ink">{results.postcode_district}</span>
                  </div>
                  <div className="text-center">
                    <span className="font-body text-xs text-ink/50 block">Eligible</span>
                    <span className="font-display text-xl font-extrabold text-brand-orange">{results.total_eligible}</span>
                  </div>
                  <div className="text-center">
                    <span className="font-body text-xs text-ink/50 block">Cost/Business</span>
                    <span className="font-display text-xl font-extrabold text-ink">£{results.cost_per_business}</span>
                  </div>
                  <div className="text-center">
                    <span className="font-body text-xs text-ink/50 block">Type</span>
                    <span className="font-body text-sm text-ink capitalize">{results.intervention_type}</span>
                  </div>
                </div>
                {/* Economic impact row */}
                {results.locked ? (
                  <div className="mt-4 pt-3 border-t border-ink/5 -mx-5 -mb-5 px-5 pb-4 pt-4 rounded-b-[4px] bg-brand-orange/[0.04] border border-brand-orange/30 border-l-0 border-r-0 border-b-0" data-testid="plan-locked-banner" ref={(el) => { if (el && !el.dataset.viewed) { el.dataset.viewed = '1'; track('paywall_view', { source: 'plan_locked_banner', feature: 'intervention_planner' }); } }}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-[4px] bg-brand-orange/15 flex items-center justify-center shrink-0">
                        <Lock className="w-4 h-4 text-brand-orange" strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-sm font-extrabold text-ink mb-1">{variant.headline_copy}</p>
                        <p className="font-body text-xs text-ink/60 leading-relaxed mb-3">{results.estimated_impact}</p>
                        <Button asChild size="sm" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs h-8" data-testid="plan-upgrade-cta" onClick={() => track('upgrade_cta_click', { source: 'plan_locked_banner', feature: 'intervention_planner' })}>
                          <Link to="/pricing">{variant.cta_copy} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : results.estimated_economic_return !== undefined && results.estimated_economic_return !== null && (
                  <div className="mt-4 pt-3 border-t border-ink/5 bg-[#2D6E4E]/3 -mx-5 -mb-5 px-5 pb-4 pt-3 rounded-b-[4px]" data-testid="plan-economic-return">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <span className="font-body text-[10px] uppercase tracking-wider text-ink/40 block">Estimated Return</span>
                        <span className="font-display text-xl font-extrabold text-[#2D6E4E]">£{(results.estimated_economic_return || 0).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="font-body text-[10px] uppercase tracking-wider text-ink/40 block">ROI Multiplier</span>
                        <span className="font-display text-xl font-extrabold text-brand-orange">{results.roi_multiplier}x</span>
                      </div>
                      <div>
                        <span className="font-body text-[10px] uppercase tracking-wider text-ink/40 block">Per Business</span>
                        <span className="font-display text-xl font-extrabold text-ink">£{(results.estimated_return_per_business || 0).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="font-body text-[10px] uppercase tracking-wider text-ink/40 block">Confidence</span>
                        <span className="font-display text-xl font-extrabold text-ink/60">~{results.confidence || 75}%</span>
                      </div>
                    </div>
                    <p className="font-body text-[10px] text-ink/35 mt-2 leading-snug">
                      Based on {results.assumptions?.oecd_multiplier || 3.2}× OECD multiplier, avg IMD {results.assumptions?.avg_imd_decile || '—'} ({results.assumptions?.imd_boost || 1}× boost), £{results.assumptions?.cost_per_intervention_benchmark || 45}/business DCMS benchmark. Adjust assumptions in the Economic Impact Estimator below.
                    </p>
                  </div>
                )}
                {!results.locked && <p className="font-body text-xs text-ink/50 mt-3">{results.estimated_impact}</p>}
              </div>

              {/* Business list */}
              <div className="solid-card p-5 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <h3 className="font-display text-lg font-extrabold text-ink mb-3">
                  Ranked Businesses ({results.businesses?.length || 0}{results.businesses_hidden > 0 && ` of ${(results.businesses?.length || 0) + results.businesses_hidden} shown`})
                </h3>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {(results.businesses || []).map((biz, i) => (
                    <div key={biz.id} className="flex items-center justify-between p-2 rounded-[4px] hover:bg-ink/3 transition-all">
                      <div className="flex items-center gap-3">
                        <span className="font-display text-sm font-extrabold text-ink/30 w-6">{i + 1}</span>
                        <div>
                          <span className="font-body text-sm text-ink">{biz.company_name}</span>
                          <span className="font-body text-xs text-ink/40 block">{biz.postcode} · {biz.sector}</span>
                        </div>
                      </div>
                      <DigitalTierBadge tier={biz.digital_tier} />
                    </div>
                  ))}
                  {results.businesses_hidden > 0 && (
                    <Link to="/pricing" data-testid="plan-businesses-hidden" className="flex items-center justify-between p-3 rounded-[4px] border border-dashed border-brand-orange/40 bg-brand-orange/[0.04] hover:bg-brand-orange/[0.08] transition-colors group">
                      <div className="flex items-center gap-3">
                        <Lock className="w-4 h-4 text-brand-orange" />
                        <div>
                          <span className="font-body text-sm font-semibold text-ink">{results.businesses_hidden} more business{results.businesses_hidden !== 1 ? 'es' : ''} ranked</span>
                          <span className="font-body text-xs text-ink/50 block">Upgrade to Pro to see the full ranked list with priority scores.</span>
                        </div>
                      </div>
                      <span className="font-body text-xs text-brand-orange font-semibold group-hover:underline">Upgrade →</span>
                    </Link>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="solid-card p-12 text-center">
              <Target className="w-10 h-10 text-ink/15 mx-auto mb-3" />
              <p className="font-body text-sm text-ink/50 max-w-md mx-auto leading-relaxed">
                Select a target area, choose an intervention type, set your budget, and click <span className="font-medium">'Generate Intervention Plan'</span>. The system will identify which businesses to prioritise, in which order, and what economic return to expect — ranked by deprivation and digital adoption gap.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ ROI CALCULATOR ═══ */}
      <ROICalculator />
    </div>
  );
}


function ROICalculator() {
  const { has } = useEntitlements();
  const isUnlocked = has('economic_impact_estimator');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  // Editable assumptions
  const [oecdMultiplier, setOecdMultiplier] = useState(3.2);
  const [sectorWeightMult, setSectorWeightMult] = useState(1.0);
  const [imdBoostMult, setImdBoostMult] = useState(1.0);
  const [costPerBiz, setCostPerBiz] = useState(45);
  const [recalculating, setRecalculating] = useState(false);

  const fetchROI = useCallback(async (params = {}) => {
    const oecd = params.oecd ?? oecdMultiplier;
    const sw = params.sw ?? sectorWeightMult;
    const imd = params.imd ?? imdBoostMult;
    const cost = params.cost ?? costPerBiz;
    try {
      setRecalculating(true);
      const { data: d } = await api.get(`/interventions/roi-calculator?oecd_multiplier=${oecd}&sector_weight_multiplier=${sw}&imd_boost_multiplier=${imd}&cost_per_intervention=${cost}`);
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRecalculating(false); }
  }, [oecdMultiplier, sectorWeightMult, imdBoostMult, costPerBiz]);

  useEffect(() => { fetchROI(); }, []); // initial load only

  const resetDefaults = () => {
    setOecdMultiplier(3.2); setSectorWeightMult(1.0); setImdBoostMult(1.0); setCostPerBiz(45);
    fetchROI({ oecd: 3.2, sw: 1.0, imd: 1.0, cost: 45 });
  };

  const exportCSV = () => {
    if (!isUnlocked) {
      toast.error('CSV export of the Economic Impact Estimator is a Pro feature.', {
        action: { label: 'Upgrade', onClick: () => window.location.href = '/pricing' },
      });
      return;
    }
    if (!data?.by_district) return;
    const rows = [['District', 'Ward', 'Invisible', 'Cost (GBP)', 'Return (GBP)', 'ROI', 'Priority']];
    data.by_district.forEach(d => {
      rows.push([d.district, `"${d.name}"`, d.trading_invisible, d.intervention_cost, d.estimated_return, `${d.roi_ratio}x`, d.priority]);
    });
    const s = data.summary;
    rows.push(['TOTAL', '', s.total_trading_invisible, s.total_intervention_cost, s.total_estimated_return, `${s.overall_roi_ratio}x`, '']);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `Economic_Impact_Estimator_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="solid-card p-12 text-center animate-pulse">
      <TrendingUp className="w-8 h-8 text-ink/10 mx-auto" />
    </div>
  );
  if (!data) return null;

  const s = data.summary;
  const districts = data.by_district || [];
  const maxReturn = Math.max(...districts.map(d => d.estimated_return), 1);

  return (
    <div className="space-y-5 mt-10" data-testid="roi-calculator">
      {/* Section header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-6 bg-[#2D6E4E] rounded-full" />
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-ink">Economic Impact Estimator</h2>
          </div>
          <p className="font-body text-sm text-ink/70 font-medium ml-3 mt-1">Estimated Economic Return</p>
          <p className="font-body text-sm text-ink/50 max-w-2xl leading-relaxed ml-3 mt-2">
            This tool shows how much local economic value could be generated from targeted digital support for businesses in the selected area.
          </p>
          <p className="font-body text-[11px] text-ink/40 max-w-2xl leading-relaxed ml-3 mt-2">
            <strong>Methodology:</strong> Estimates combine the number of digitally under-served businesses, local deprivation levels, sector-specific growth potential, and evidence-based economic multipliers from DCMS programme benchmarks and OECD Digital Economy research. Results are presented as a range with a confidence level. They are designed to guide investment decisions, not to predict specific outcomes.
          </p>
        </div>
        <Button variant="outline" size="sm" className="font-body text-xs gap-1" onClick={exportCSV} data-testid="roi-export-csv">
          {isUnlocked ? <Download className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5 text-brand-orange" />} Export CSV
          {!isUnlocked && <span className="text-[9px] uppercase tracking-wider text-brand-orange/80 ml-0.5">Pro</span>}
        </Button>
      </div>

      {/* ── Editable assumptions panel ── */}
      <div className="solid-card p-5 border border-ink/10" data-testid="roi-assumptions-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-sm font-extrabold text-ink">Adjust Assumptions</h3>
            <p className="font-body text-[11px] text-ink/50 mt-0.5">Change multipliers to stress-test outcomes. Every input here feeds directly into the estimate below and the Intervention Planner's economic-return block.</p>
          </div>
          <Button variant="ghost" size="sm" className="font-body text-[10px] text-ink/40 hover:text-ink" onClick={resetDefaults} data-testid="roi-reset">Reset to defaults</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/50">OECD Multiplier</Label>
            <Select value={String(oecdMultiplier)} onValueChange={(v) => { const n = parseFloat(v); setOecdMultiplier(n); fetchROI({ oecd: n }); }}>
              <SelectTrigger className="font-body text-xs mt-1 h-9" data-testid="roi-oecd-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(data.presets?.oecd_multiplier_presets || []).map(p => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="font-body text-[9px] text-ink/35 mt-1">Economic multiplier per £ invested</p>
          </div>
          <div>
            <Label className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/50">Sector Weight Mix</Label>
            <Select value={String(sectorWeightMult)} onValueChange={(v) => { const n = parseFloat(v); setSectorWeightMult(n); fetchROI({ sw: n }); }}>
              <SelectTrigger className="font-body text-xs mt-1 h-9" data-testid="roi-sector-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(data.presets?.sector_weight_presets || []).map(p => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="font-body text-[9px] text-ink/35 mt-1">B2C-heavy sectors return more per £</p>
          </div>
          <div>
            <Label className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/50">IMD Deprivation Boost</Label>
            <Select value={String(imdBoostMult)} onValueChange={(v) => { const n = parseFloat(v); setImdBoostMult(n); fetchROI({ imd: n }); }}>
              <SelectTrigger className="font-body text-xs mt-1 h-9" data-testid="roi-imd-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(data.presets?.imd_boost_presets || []).map(p => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="font-body text-[9px] text-ink/35 mt-1">Larger = more weight on deprived areas</p>
          </div>
          <div>
            <Label className="font-body text-[10px] uppercase tracking-[0.1em] text-ink/50">Cost / Business (£)</Label>
            <Input type="number" min={10} max={500} value={costPerBiz}
              onChange={(e) => setCostPerBiz(Number(e.target.value))}
              onBlur={() => fetchROI({ cost: costPerBiz })}
              className="font-body text-xs mt-1 h-9" data-testid="roi-cost-input" />
            <p className="font-body text-[9px] text-ink/35 mt-1">DCMS programme benchmark: £45</p>
          </div>
        </div>
        {recalculating && (
          <p className="font-body text-[10px] text-brand-orange mt-3 flex items-center gap-1"><TrendingUp className="w-3 h-3 animate-pulse" /> Recalculating…</p>
        )}
      </div>

      {/* Economic Impact headline */}
      <div className="bg-[#090909] text-white p-6 rounded-[4px] relative overflow-hidden" data-testid="roi-headline">
        {!isUnlocked && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-b from-[#090909]/95 to-[#090909] backdrop-blur-[2px]" data-testid="roi-headline-lock">
            <div className="text-center max-w-md px-6">
              <div className="w-12 h-12 rounded-full bg-brand-orange/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-5 h-5 text-brand-orange" strokeWidth={2} />
              </div>
              <p className="font-display text-lg font-extrabold text-white mb-2">Economic projection is a Pro feature</p>
              <p className="font-body text-xs text-white/60 mb-4 leading-relaxed">
                Adjust the assumptions above to model your scenario. Upgrade to Pro to compute the projected economic return,
                ROI multiplier, and per-district breakdown.
              </p>
              <Button asChild size="sm" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs h-9 px-4" data-testid="roi-headline-upgrade-cta">
                <Link to="/pricing">Upgrade to Pro <Sparkles className="w-3.5 h-3.5 ml-1.5" /></Link>
              </Button>
            </div>
          </div>
        )}
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 ${!isUnlocked ? 'opacity-30' : ''}`}>
          <div>
            <div className="font-body text-[9px] uppercase tracking-wider text-white/30">Total Investment</div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold mt-1">
              {isUnlocked ? <><span className="text-white/40 text-lg">£</span>{s.total_intervention_cost?.toLocaleString()}</> : <span className="text-white/30">£ — — —</span>}
            </div>
          </div>
          <div>
            <div className="font-body text-[9px] uppercase tracking-wider text-white/30">Estimated Return (range)</div>
            <div className="font-display text-xl sm:text-2xl font-extrabold text-[#2D6E4E] mt-1">
              {isUnlocked && s.total_estimated_return
                ? <>£{(s.total_estimated_return * 0.85 / 1000000).toFixed(1)}M – £{(s.total_estimated_return * 1.15 / 1000000).toFixed(1)}M</>
                : <span className="text-white/30">£ — . — M</span>}
            </div>
            {isUnlocked && s.total_estimated_return && <div className="font-body text-[10px] text-white/40 mt-0.5">Central: £{(s.total_estimated_return / 1000000).toFixed(1)}M</div>}
          </div>
          <div>
            <div className="font-body text-[9px] uppercase tracking-wider text-white/30">ROI Multiplier</div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#FE4A2A] mt-1">{isUnlocked ? <>{s.overall_roi_ratio}x</> : <span className="text-white/30">— x</span>}</div>
            {isUnlocked && <div className="font-body text-[10px] text-white/40 mt-0.5">Confidence: ~75%</div>}
          </div>
          <div>
            <div className="font-body text-[9px] uppercase tracking-wider text-white/30">Businesses Targeted</div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold mt-1">{(s.total_trading_invisible || 0).toLocaleString()}</div>
          </div>
        </div>
        {isUnlocked && (
          <div className="mt-4 pt-3 border-t border-white/10 space-y-1.5">
            <p className="font-body text-[10px] text-white/25">{s.methodology}</p>
            <p className="font-body text-[10px] text-white/35">
              <strong className="text-white/60">Sensitivity:</strong> If intervention effectiveness is 10% lower than projected, estimated return decreases to £{(s.total_estimated_return * 0.9 / 1000000).toFixed(1)}M.
            </p>
            <p className="font-body text-[9px] text-white/20 italic leading-snug">
              Estimates are based on national benchmarks and modelling assumptions. Actual outcomes will vary depending on delivery approach, business engagement levels, and local market conditions. Results should be used to guide investment decisions, not to guarantee specific returns.
            </p>
          </div>
        )}
      </div>

      {/* District ROI bar chart */}
      {isUnlocked ? (
        <div className="solid-card p-5" data-testid="roi-district-chart">
          <h3 className="font-body text-[10px] uppercase tracking-wider text-ink/30 mb-4">Estimated Return by District</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={districts} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="district" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <YAxis tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }}
                tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 2, border: '1px solid #eee' }}
                formatter={(v, name) => [`£${v.toLocaleString()}`, name === 'estimated_return' ? 'Est. Return' : name === 'intervention_cost' ? 'Cost' : name]} />
              <Bar dataKey="intervention_cost" name="Cost" fill="#B8B4AD" radius={[2, 2, 0, 0]} barSize={18} />
              <Bar dataKey="estimated_return" name="Est. Return" radius={[2, 2, 0, 0]} barSize={18}>
                {districts.map((d, i) => (
                  <Cell key={i} fill={d.roi_ratio >= 6 ? '#2D6E4E' : d.roi_ratio >= 5 ? '#6B9E56' : '#8A5A14'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-5 justify-center mt-2">
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#B8B4AD]" /><span className="font-body text-[10px] text-ink/40">Intervention Cost</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#2D6E4E]" /><span className="font-body text-[10px] text-ink/40">Est. Return (6x+)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#6B9E56]" /><span className="font-body text-[10px] text-ink/40">Est. Return (5-6x)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#8A5A14]" /><span className="font-body text-[10px] text-ink/40">Est. Return (&lt;5x)</span></div>
          </div>
        </div>
      ) : (
        <div className="solid-card p-12 flex flex-col items-center justify-center text-center" data-testid="roi-district-chart-lock">
          <Lock className="w-7 h-7 text-brand-orange mb-3" strokeWidth={1.5} />
          <p className="font-display text-sm font-extrabold text-ink mb-1">District-level economic projections</p>
          <p className="font-body text-xs text-ink/55 mb-4 max-w-md">Pro plan unlocks the full Estimated Return by District chart with cost, return, and ROI for all 9 postcode districts.</p>
          <Button asChild size="sm" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs h-9" data-testid="roi-chart-upgrade-cta">
            <Link to="/pricing">Upgrade to Pro <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
          </Button>
        </div>
      )}

      {/* District cards with expandable sector details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="roi-district-cards">
        {districts.map((d, i) => {
          const isExpanded = expanded === d.district;
          return (
            <div key={i} className="solid-card overflow-hidden" data-testid={`roi-district-${d.district}`}>
              <button
                className="w-full p-4 text-left hover:bg-ink/[0.01] transition-colors"
                onClick={() => isUnlocked && setExpanded(isExpanded ? null : d.district)}
                disabled={!isUnlocked}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display text-lg font-extrabold text-ink">{d.district}</div>
                    <div className="font-body text-[10px] text-ink/40">{d.name}</div>
                  </div>
                  <span className={`font-display text-xs font-bold px-2 py-0.5 rounded-full ${
                    d.priority === 'CRITICAL' ? 'bg-[#8A1A1A]/10 text-[#8A1A1A]' :
                    d.priority === 'HIGH' ? 'bg-[#8A5A14]/10 text-[#8A5A14]' :
                    'bg-ink/5 text-ink/40'
                  }`}>{d.priority}</span>
                </div>

                {/* ROI bar */}
                <div className="h-1.5 bg-ink/[0.04] rounded-full overflow-hidden mb-3">
                  {isUnlocked && (
                    <div className="h-full bg-[#2D6E4E] rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (d.estimated_return / maxReturn) * 100)}%` }} />
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="font-body text-[9px] text-ink/30">Businesses</div>
                    <div className="font-display text-sm font-extrabold text-[#8A1A1A]">{d.trading_invisible}</div>
                  </div>
                  <div>
                    <div className="font-body text-[9px] text-ink/30">Cost</div>
                    <div className="font-display text-sm font-extrabold text-ink">{isUnlocked ? `£${d.intervention_cost?.toLocaleString()}` : <span className="text-ink/30">£ — — —</span>}</div>
                  </div>
                  <div>
                    <div className="font-body text-[9px] text-ink/30">Return</div>
                    <div className="font-display text-sm font-extrabold text-[#2D6E4E]">{isUnlocked ? `£${d.estimated_return?.toLocaleString()}` : <span className="text-ink/30">£ — — —</span>}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-2 pt-2 border-t border-ink/[0.04]">
                  <span className="font-display text-lg font-extrabold text-[#FE4A2A]">
                    {isUnlocked ? <>{d.roi_ratio}x <span className="font-body text-[10px] font-normal text-ink/30">ROI</span></> : <span className="font-body text-[10px] font-semibold text-brand-orange flex items-center gap-1"><Lock className="w-3 h-3" /> Pro</span>}
                  </span>
                  {isUnlocked && (isExpanded ? <ChevronUp className="w-4 h-4 text-ink/20" /> : <ChevronDown className="w-4 h-4 text-ink/20" />)}
                </div>
              </button>

              {/* Expanded sector detail */}
              {isUnlocked && isExpanded && d.top_sectors?.length > 0 && (
                <div className="px-4 pb-4 border-t border-ink/[0.04] animate-fade-in">
                  <div className="font-body text-[9px] uppercase tracking-wider text-ink/25 pt-3 mb-2">Top sectors by ROI</div>
                  {d.top_sectors.map((sec, si) => (
                    <div key={si} className="flex items-center justify-between py-1.5 border-b border-ink/[0.03] last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="font-body text-[11px] text-ink/70 truncate">{sec.sector}</div>
                        <div className="font-body text-[9px] text-ink/25">{sec.count} biz | weight {sec.sector_weight}x | IMD {sec.avg_imd}</div>
                      </div>
                      <div className="text-right ml-3">
                        <div className="font-display text-xs font-bold text-[#2D6E4E]">£{sec.roi_per_business}/biz</div>
                        <div className="font-body text-[9px] text-ink/25">£{sec.total_roi.toLocaleString()} total</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!isUnlocked && data.districts_hidden > 0 && (
          <Link to="/pricing" className="rounded-[6px] border-2 border-dashed border-brand-orange/40 bg-brand-orange/[0.04] hover:bg-brand-orange/[0.08] p-5 flex flex-col items-center justify-center text-center transition-colors group" data-testid="roi-districts-hidden">
            <Lock className="w-5 h-5 text-brand-orange mb-2" strokeWidth={1.5} />
            <p className="font-display text-sm font-extrabold text-ink">+{data.districts_hidden} more districts</p>
            <p className="font-body text-xs text-ink/55 mt-1 mb-3">Full ROI breakdown for all 9 districts</p>
            <span className="font-body text-xs text-brand-orange font-semibold group-hover:underline">Upgrade to Pro →</span>
          </Link>
        )}
      </div>

      {/* Methodology */}
      <div className="bg-ink/[0.02] border border-ink/5 p-4 rounded-[4px]" data-testid="roi-methodology">
        <h3 className="font-body text-xs font-medium text-ink mb-2 flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-[#FE4A2A]" /> Methodology
        </h3>
        <p className="font-body text-[11px] text-ink/40 leading-relaxed">
          <strong className="text-ink/60">Formula:</strong> £45/business (DCMS benchmark) x sector weight (B2C sectors score higher — Accommodation 3.2x, Retail 3.0x, Real Estate 0.5x) x IMD deprivation boost (lower decile = higher boost) x connectivity risk factor (High +30%) x 3.2 OECD multiplier.
          Districts are ranked by ROI ratio. CRITICAL = 500+ invisible businesses, HIGH = 200+, MEDIUM = &lt;200.
          <strong className="text-ink/60 ml-1">Sources:</strong> DCMS Digital Skills Report, OECD Digital Economy Outlook, ONS IoD 2019, Ofcom Connected Nations.
        </p>
      </div>
    </div>
  );
}
